import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { IHoldingsReportEntry } from "interfaces";
import dayjs from "dayjs";

interface HoldingsReportParams {
  holdings: IHoldingsReportEntry[];
  vesselName: string;
  imoNumber: string;
  ukhoLicenceId: string;
}

export const generateHoldingsPdf = async ({
  holdings,
  vesselName,
  imoNumber,
  ukhoLicenceId,
}: HoldingsReportParams): Promise<Blob> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Load logo
  const logoBase64 = await loadLogoAsBase64();

  // Header background
  doc.setFillColor(240, 248, 255); // Light blue background
  doc.rect(0, 0, pageWidth, 25, "F");

  // Header border
  doc.setDrawColor(41, 128, 185);
  doc.setLineWidth(0.5);
  doc.line(0, 25, pageWidth, 25);

  // Logo with white background for visibility
  if (logoBase64) {
    doc.setFillColor(42, 19, 46);
    doc.rect(12, 6, 42, 14, "F");
    doc.addImage(logoBase64, "PNG", 14, 8, 38, 10);
  }

  // Title
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(41, 128, 185);
  doc.text("AVCS Holdings Report", pageWidth - 14, 16, { align: "right" });
  doc.setTextColor(0, 0, 0);

  // Vessel info
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(`Vessel: ${vesselName}`, 14, 34);
  doc.text(`IMO: ${imoNumber}`, 14, 40);
  doc.text(`Licence ID: ${ukhoLicenceId}`, 14, 46);
  doc.text(`Generated: ${dayjs().format("DD-MMM-YYYY HH:mm:ss")}`, pageWidth - 14, 34, { align: "right" });

  let yPosition = 56;

  // Holdings data
  holdings.forEach((holding, index) => {
    // Check if we need a new page
    if (yPosition > 260) {
      doc.addPage();
      yPosition = 20;
    }

    // Product header
    doc.setFillColor(240, 240, 240);
    doc.rect(14, yPosition - 5, pageWidth - 28, 8, "F");
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(`${holding.productCode} - ${holding.productTitle}`, 16, yPosition);

    // Expiry date
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const expiryText = `Expires: ${dayjs(holding.expiryDate).format("DD-MMM-YYYY")}`;
    doc.text(expiryText, pageWidth - 16, yPosition, { align: "right" });

    yPosition += 8;

    // Cells table
    if (holding.cells && holding.cells.length > 0) {
      const tableData = holding.cells.map((cell) => [
        cell.cellCode,
        cell.cellTitle,
        cell.latestEditionNumber,
        cell.latestUpdateNumber,
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [["Cell Code", "Cell Title", "Edition", "Update"]],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: [41, 128, 185],
          fontSize: 9,
          fontStyle: "bold",
        },
        bodyStyles: {
          fontSize: 8,
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: "auto" },
          2: { cellWidth: 20, halign: "center" },
          3: { cellWidth: 20, halign: "center" },
        },
        margin: { left: 14, right: 14 },
      });

      // Get the final Y position after the table
      yPosition = (doc as any).lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(9);
      doc.setTextColor(128, 128, 128);
      doc.text("No cells available", 16, yPosition);
      doc.setTextColor(0, 0, 0);
      yPosition += 10;
    }
  });

  // Footer on each page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `© ${new Date().getFullYear()} e-Mariner NavSolutions`,
      14,
      doc.internal.pageSize.getHeight() - 10
    );
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - 14,
      doc.internal.pageSize.getHeight() - 10,
      { align: "right" }
    );
  }

  return doc.output("blob");
};

const loadLogoAsBase64 = (): Promise<string | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } else {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = "/emariner-logo-pro.png";
  });
};
